import type { OptimizationAvailabilities } from 'watchtower-ui/pages/OptimizationPages/OptimizationTypeUtils';
import { formatDate, parseDateString } from 'watchtower-ui/utils/dateUtils';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && Object.prototype.toString.call(value) === '[object Object]';

export const isString = (value: unknown): value is string => typeof value === 'string';

export const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every(isString);

export type ParentVehicleEntry = {
  vehicle: string[];
};

export type ParentVehicleMap = Record<string, ParentVehicleEntry>;

export const isParentVehicleEntry = (value: unknown): value is ParentVehicleEntry => {
  if (!isRecord(value)) {
    return false;
  }

  return isStringArray(Reflect.get(value, 'vehicle'));
};

export const isParentVehicleMap = (value: unknown): value is ParentVehicleMap => {
  if (!isRecord(value)) {
    return false;
  }

  return Object.values(value).every(isParentVehicleEntry);
};

export const toParentVehicleMap = (value: unknown): ParentVehicleMap => (isParentVehicleMap(value) ? value : {});

export const isPresent = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

type NestedAvailabilityMap = Record<string, Record<string, string[]>>;

const isStringArrayRecord = (value: unknown): value is Record<string, string[]> =>
  isRecord(value) && Object.values(value).every(isStringArray);

const isNestedAvailabilityMap = (value: unknown): value is NestedAvailabilityMap =>
  isRecord(value) && Object.values(value).every(isStringArrayRecord);

const isAvailabilityMap = (value: unknown): value is OptimizationAvailabilities['geo'] =>
  isRecord(value) && Object.values(value).every((item) => isStringArray(item) || isNestedAvailabilityMap(item));

const isAvailabilitySelection = (value: unknown): value is NonNullable<OptimizationAvailabilities['region']> =>
  isString(value) || isStringArray(value) || isNestedAvailabilityMap(value);

const isValidDateString = (value: unknown): value is string => {
  if (!isString(value) || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const parsedDate = parseDateString(value);
  return parsedDate !== null && formatDate(parsedDate) === value;
};

const isValidDateRange = (value: unknown): value is [string, string] =>
  Array.isArray(value) && value.length === 2 && value.every(isValidDateString);

export const isValidAvailabilitiesPayload = (value: unknown): value is OptimizationAvailabilities => {
  if (!isRecord(value)) {
    return false;
  }

  const invalidFields = [
    !isAvailabilityMap(value.geo),
    !isAvailabilityMap(value.brand),
    !isAvailabilityMap(value.signal),
    !isValidDateString(value.min_date),
    !isValidDateString(value.max_date),
    !isValidDateRange(value.default_date_range),
    value.region !== undefined && !isAvailabilitySelection(value.region),
    value.state !== undefined && !isAvailabilitySelection(value.state),
    value.spread_types !== undefined && !isStringArray(value.spread_types),
  ];

  return !invalidFields.some(Boolean);
};
